/**
 * ListRow — primitive Editorial Operations (HiFi.3).
 *
 * Brique principale des listes de planning, repas, transmissions,
 * frais, sac à dos, etc. Grille horizontale :
 *
 *   [10 px dot] [56 px time] [1fr title + meta] [auto tag] [auto author] [16 px chevron]
 *
 *   ●  08:30   Tom · départ école          [EVENT]   JULIEN  >
 *              à 8 min · à pied
 *
 * Toutes les colonnes auto sont optionnelles. Le `time` est en
 * DM Serif 17 px coloré --accent-deep — c'est lui qui « tient »
 * visuellement la ligne. Le titre est en Lato 700.
 *
 * Le composant gère :
 *   - hover (--rule-soft, 80 ms ease)
 *   - état done : barré + opacité réduite (sur le time et le title)
 *   - dernier de la liste : pas de border-bottom
 *
 * Pas de click handler obligatoire — la row reste interactive (button)
 * mais peut être posée sans `onClick` (cas d'affichage read-only).
 */
import { html, type TemplateResult } from 'lit-html';
import { classMap } from 'lit-html/directives/class-map.js';
import styles from './editorial.module.css';
import { Icon, Icons } from './icons.js';
import { tag, type TagKind } from './Tag.js';

export interface ListRowTag {
  label: string;
  kind?: TagKind;
}

export interface ListRowProps {
  /** Texte de gauche en DM Serif 17 px. Ex. « 08:30 », « ce soir », « mar. ». */
  time: string;
  /** Titre principal en Lato 700. */
  title: string;
  /** Sous-ligne meta en --ink-soft 12 px. */
  meta?: string;
  /** Couleur du dot (souvent la couleur d'un membre ou --warn pour task). */
  dotColor?: string;
  /** Tag pill optionnel. */
  rowTag?: ListRowTag;
  /** Auteur kicker à droite (« CAMILLE », « JULIEN »). */
  author?: string;
  /** Click handler — laisser undefined pour read-only. */
  onClick?: (e: Event) => void;
  /** Marque comme dernière row (pas de border-bottom). */
  last?: boolean;
  /** Tâche done — barré + atténué. */
  done?: boolean;
  /** testid optionnel pour les E2E. */
  testid?: string;
}

export function listRow(props: ListRowProps): TemplateResult {
  const {
    time,
    title,
    meta,
    dotColor = 'var(--accent)',
    rowTag,
    author,
    onClick,
    last,
    done,
    testid,
  } = props;
  return html`<button
    type="button"
    class=${classMap({
      [styles.listRow!]: true,
      [styles.listRowLast!]: !!last,
    })}
    @click=${onClick}
    ?disabled=${!onClick}
    style="${onClick ? '' : 'cursor: default;'}"
    data-testid=${testid ?? 'list-row'}
  >
    <span class=${styles.listRowDot} style="background: ${dotColor}"></span>
    <span
      class=${classMap({
        [styles.listRowTime!]: true,
        [styles.listRowTimeDone!]: !!done,
      })}
      >${time}</span
    >
    <span class=${styles.listRowBody}>
      <div
        class=${classMap({
          [styles.listRowTitle!]: true,
          [styles.listRowTitleDone!]: !!done,
        })}
      >
        ${title}
      </div>
      ${meta ? html`<div class=${styles.listRowMeta}>${meta}</div>` : null}
    </span>
    ${rowTag
      ? tag({ label: rowTag.label, ...(rowTag.kind ? { kind: rowTag.kind } : {}) })
      : html`<span></span>`}
    ${author ? html`<span class=${styles.listRowAuthor}>${author}</span>` : html`<span></span>`}
    <span class=${styles.listRowChevron}>${Icon(Icons.chevronRight, 14)}</span>
  </button>`;
}
